import CryptoJS from "crypto-js";
import AzureBlobStorageClient from '../azure/AzureBlobStorage.js';
import consoleLogger from './consoleLogger.js';

/**
 * Interview Log Uploader
 * Handles uploading console logs to Azure blob storage for each interview session
 */

class InterviewLogUploader {
  constructor(sasUrl) {
    this.azureClient = new AzureBlobStorageClient(sasUrl);
  }

  /**
   * Upload interview logs to Azure blob storage
   * @param {string} meetingId - The meeting/interview ID
   * @param {string} candidateName - The candidate's name
   * @returns {Promise<{success: boolean, filename: string, url?: string, error?: string}>}
   */
  async uploadInterviewLogs(meetingId, candidateName) {
    try {
      // Set session info in logger if not already set
      consoleLogger.setSessionInfo(meetingId, candidateName);
      
      // Generate filename
      const filename = consoleLogger.generateInterviewLogFilename(meetingId);
      
      // Create blob with interview logs
      const logBlob = consoleLogger.createInterviewLogBlob();
      
      // Define the path in Azure storage (logs folder)
      const blobPath = `BrowserLogs/${filename}`;
      
      console.log(`Uploading interview logs to Azure: ${blobPath}`);
      
      // Upload to Azure blob storage
      const uploadResult = await this.uploadToAzure(logBlob, blobPath);
      
      if (uploadResult.success) {
        console.log(`Interview logs uploaded successfully: ${filename}`);
        return {
          success: true,
          filename: filename,
          blobPath: blobPath
        };
      } else {
        console.error('Failed to upload interview logs:', uploadResult.error);
        return {
          success: false,
          filename: filename,
          error: uploadResult.error
        };
      }
    } catch (error) {
      console.error('Error uploading interview logs:', error);
      return {
        success: false,
        filename: null,
        error: error.message
      };
    }
  }

  /**
   * Upload blob to Azure storage
   * @param {Blob} blob - The blob to upload
   * @param {string} blobPath - The path/name for the blob in Azure storage
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  async uploadToAzure(blob, blobPath) {
    try {
      
      // Fallback to direct Azure client usage
      if (this.azureClient) {
        await this.azureClient.upload(blob, blobPath);
        return {
          success: true,
        };
      }
      
      // If neither method is available, throw an error
      throw new Error('No Azure upload method available');
      
    } catch (error) {
      console.error('Azure upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload logs with retry mechanism
   * @param {string} meetingId - The meeting/interview ID
   * @param {string} candidateName - The candidate's name
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Promise<{success: boolean, filename: string, url?: string, error?: string}>}
   */
  async uploadInterviewLogsWithRetry(meetingId, candidateName, maxRetries = 3) {
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Attempting to upload interview logs (attempt ${attempt}/${maxRetries})`);
        
        const result = await this.uploadInterviewLogs(meetingId, candidateName);
        
        if (result.success) {
          return result;
        }
        
        lastError = result.error;
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          console.log(`Upload failed, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
      } catch (error) {
        lastError = error.message;
        console.error(`Upload attempt ${attempt} failed:`, error);
        
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    return {
      success: false,
      filename: null,
      error: `Failed after ${maxRetries} attempts. Last error: ${lastError}`
    };
  }

  /**
   * Save logs locally as backup before upload
   * @param {string} meetingId - The meeting/interview ID
   * @param {string} candidateName - The candidate's name
   * @returns {string} - The filename of the downloaded backup
   */
  saveLocalBackup(meetingId, candidateName) {
    consoleLogger.setSessionInfo(meetingId, candidateName);
    return consoleLogger.downloadInterviewLogs();
  }
}

// Create singleton instance
const interviewLogUploader = new InterviewLogUploader();

export default interviewLogUploader;
export { InterviewLogUploader };

// Global function for easy access
window.uploadInterviewLogs = (meetingId, candidateName) => 
  interviewLogUploader.uploadInterviewLogs(meetingId, candidateName);
